/**
 * Language Switcher Floating Action Button (FAB)
 *
 * Allows system admins to quickly switch the UI language without navigating to Settings.
 * Only appears when language testing mode is activated from Settings > Account.
 * Uses the same pattern as RoleTestingFab.
 *
 * This implements a temporary language override stored in localStorage.
 * When testing is exited, the language reverts to the user's database preference.
 */
import CloseIcon from "@mui/icons-material/Close";
import LanguageIcon from "@mui/icons-material/Language";
import {
  Button,
  Fab,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { changeLanguage, supportedLanguages, SupportedLanguage, getCurrentLanguage } from "../i18n/config";
import { useLanguageTesting } from "../stores/useLanguageTesting";

export const LanguageSwitcherFab: React.FC = () => {
  const { t } = useTranslation();
  const { isActive, stopLanguageTesting, setLanguage } = useLanguageTesting();
  const [expanded, setExpanded] = React.useState(false);

  // Only show when language testing is active (activated from Settings > Account)
  if (!isActive) {
    return null;
  }

  const handleLanguageChange = async (lang: SupportedLanguage) => {
    await changeLanguage(lang);           // Update i18n immediately
    setLanguage(lang);                    // Save override to localStorage
    setExpanded(false);
  };

  const handleExit = () => {
    stopLanguageTesting();                // Clears isActive + selectedLanguage
    setExpanded(false);
    // Note: useLanguageSync will detect selectedLanguage=null and revert to database preference
  };

  const currentLang = getCurrentLanguage();  // Read from i18n (no local state)
  const currentLanguageInfo = supportedLanguages.find(l => l.code === currentLang);

  return (
    <>
      {expanded && (
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: (theme) => theme.spacing(10),
            right: (theme) => theme.spacing(10),
            p: 2,
            zIndex: 1300,
            minWidth: 220,
          }}
        >
          <Stack direction="column" spacing={2}>
            <Typography variant="subtitle1" fontWeight="bold">
              {t('widgets.languageSwitcher.title')}
            </Typography>

            <FormControl fullWidth size="small">
              <InputLabel>{t('widgets.languageSwitcher.selectLabel')}</InputLabel>
              <Select
                value={currentLang}
                onChange={(e) => handleLanguageChange(e.target.value as SupportedLanguage)}
                label={t('widgets.languageSwitcher.selectLabel')}
              >
                {supportedLanguages.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.nativeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              color="secondary"
              startIcon={<CloseIcon />}
              onClick={handleExit}
              fullWidth
            >
              {t('widgets.languageSwitcher.exitButton')}
            </Button>
          </Stack>
        </Paper>
      )}

      <Tooltip
        title={
          expanded
            ? t('widgets.languageSwitcher.close')
            : t('widgets.languageSwitcher.tooltip', { language: currentLanguageInfo?.nativeName })
        }
      >
        <Fab
          color="primary"
          onClick={() => setExpanded(!expanded)}
          sx={{
            position: "fixed",
            bottom: (theme) => theme.spacing(2),
            right: (theme) => theme.spacing(10),
          }}
        >
          {expanded ? <CloseIcon /> : <LanguageIcon />}
        </Fab>
      </Tooltip>
    </>
  );
};
